import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ConfirmDialog from '../ui/ConfirmDialog';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { useAutoSave } from '../../hooks/useAutoSave';
import { apiService } from '../../services/api.service';
import { USER_ROLES, USER_ROLE_LABELS, UserRole } from '../../types';

/**
 * Les champs d'un compte, éditables dans le panneau latéral, sauvegarde
 * automatique (règle du 2026-09-22) : rien ici ne rend public ni n'écrit chez
 * quelqu'un d'autre. Champ texte au blur et seulement s'il a bougé, plan et
 * exemption au changement, retour en arrière sur refus.
 *
 * Deux exceptions, voulues :
 *  - le RÔLE demande une confirmation (décision Bryan, 2026-09-29) : passer un
 *    compte en organisateur exige une organisation, le rétrograder en
 *    participant l'en détache ;
 *  - un email déjà pris est refusé par le serveur en 409 en nommant le compte
 *    en conflit : le message s'affiche SOUS le champ, et l'ancienne adresse
 *    revient.
 */
export interface EditableUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  org_name?: string | null;
  org_slug?: string | null;
  plan?: string | null;
  anonymization_exempt?: boolean | null;
}

interface Patch {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  org_name?: string;
  org_slug?: string;
  plan?: string;
  anonymization_exempt?: boolean;
}

interface Props {
  user: EditableUser;
  /** Après chaque écriture acceptée : le parent met la liste et la fiche à jour. */
  onSaved: (patch: Patch) => void;
  /** Le rôle a changé : la fiche se recharge (l'organisation a pu être détachée). */
  onRoleChanged: () => void;
}

const AdminUserEditor: React.FC<Props> = ({ user, onSaved, onRoleChanged }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(user.email);
  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [orgName, setOrgName] = useState(user.org_name ?? '');
  const [orgSlug, setOrgSlug] = useState(user.org_slug ?? '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null);
  const [savingRole, setSavingRole] = useState(false);

  const autoSave = useAutoSave<Patch>(
    async (patch) => {
      await apiService.put(`/admin/users/${user.id}`, patch);
      onSaved(patch);
    },
    {
      onError: (error: any) => {
        const detail: string | undefined = error?.response?.data?.detail;
        if (error?.response?.status === 409) setEmailError(detail || t('admin.users.emailTakenHint'));
      },
    },
  );
  const { setBaseline } = autoSave;

  // Le serveur porte ces valeurs : c'est la référence, posée à l'ouverture et
  // à chaque changement de compte affiché.
  const shownUser = useRef(user.id);
  useEffect(() => {
    setBaseline({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      org_name: user.org_name ?? '',
      org_slug: user.org_slug ?? '',
      plan: user.plan ?? 'basic',
      anonymization_exempt: user.anonymization_exempt ?? false,
    });
    if (shownUser.current !== user.id) {
      shownUser.current = user.id;
      setEmail(user.email);
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setOrgName(user.org_name ?? '');
      setOrgSlug(user.org_slug ?? '');
      setEmailError(null);
      setRoleError(null);
    }
  }, [user, setBaseline]);

  const saveText = (key: 'email' | 'first_name' | 'last_name' | 'org_name' | 'org_slug', value: string, revertTo: string, revert: (v: string) => void) => {
    const trimmed = value.trim();
    if (!trimmed) { revert(revertTo); return; }
    if (key === 'email') setEmailError(null);
    void autoSave.commitIfChanged(key, trimmed, () => revert(revertTo));
  };

  const confirmRole = async () => {
    if (!pendingRole) return;
    setSavingRole(true);
    setRoleError(null);
    const previous = user.role;
    try {
      await apiService.put(`/admin/users/${user.id}`, { role: pendingRole });
      setPendingRole(null);
      onRoleChanged();
    } catch (error: any) {
      setRoleError(error?.response?.data?.detail || t('errors.generic'));
      setPendingRole(null);
      // Rien n'a été écrit : le sélecteur montre toujours `previous`.
      void previous;
    } finally {
      setSavingRole(false);
    }
  };

  const hasOrg = !!user.org_name;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="user_email">Email</Label>
        <Input
          id="user_email"
          type="email"
          value={email}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? 'user_email_error' : undefined}
          onChange={(e) => { setEmailError(null); setEmail(e.target.value); }}
          onBlur={() => saveText('email', email, user.email, setEmail)}
        />
        {emailError && (
          <p id="user_email_error" className="text-sm text-destructive">
            <span className="font-semibold">{t('admin.users.emailTaken')}</span> · {emailError}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="user_first_name">Prénom</Label>
          <Input id="user_first_name" value={firstName}
                 onChange={(e) => setFirstName(e.target.value)}
                 onBlur={() => saveText('first_name', firstName, user.first_name, setFirstName)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="user_last_name">Nom</Label>
          <Input id="user_last_name" value={lastName}
                 onChange={(e) => setLastName(e.target.value)}
                 onBlur={() => saveText('last_name', lastName, user.last_name, setLastName)} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Rôle</Label>
        <Select value={user.role} onValueChange={(value) => { if (value !== user.role) setPendingRole(value as UserRole); }}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {USER_ROLES.map((role) => (
              <SelectItem key={role} value={role}>{USER_ROLE_LABELS[role]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {roleError && <p className="text-sm text-destructive">{roleError}</p>}
      </div>

      {hasOrg && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="user_org_name">Organisation</Label>
            <Input id="user_org_name" value={orgName}
                   onChange={(e) => setOrgName(e.target.value)}
                   onBlur={() => saveText('org_name', orgName, user.org_name ?? '', setOrgName)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="user_org_slug">Slug de l'organisation</Label>
            <Input id="user_org_slug" value={orgSlug}
                   onChange={(e) => setOrgSlug(e.target.value)}
                   onBlur={() => saveText('org_slug', orgSlug, user.org_slug ?? '', setOrgSlug)} />
            <p className="text-xs text-muted-foreground">Identifiant URL de l'organisation (ex : hyrox-farm)</p>
          </div>
          <div className="space-y-1.5">
            <Label>Plan</Label>
            <Select value={user.plan || 'basic'}
                    onValueChange={(value) => { void autoSave.commitIfChanged('plan', value); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Premium : accès aux événements privés</p>
          </div>
        </>
      )}

      <div className="space-y-2 rounded-md border border-border p-3">
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor="user_anonymization_exempt" className="cursor-pointer">
            Exempt d'anonymisation (staging)
          </Label>
          <Switch
            id="user_anonymization_exempt"
            checked={user.anonymization_exempt ?? false}
            onCheckedChange={(checked) => { void autoSave.commitIfChanged('anonymization_exempt', checked); }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Ce compte garde son email et son mot de passe réels lors de la copie hebdomadaire
          prod → staging, pour pouvoir s'y connecter et tester. Le nom et le téléphone restent
          anonymisés. À réserver aux testeurs : chaque exemption laisse une adresse réelle sur staging.
        </p>
      </div>

      <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />

      <ConfirmDialog
        open={pendingRole !== null}
        onOpenChange={(open) => { if (!open) setPendingRole(null); }}
        title="Changer le rôle"
        message={pendingRole ? `Passer ce compte en « ${USER_ROLE_LABELS[pendingRole]} » ?` : ''}
        description={
          pendingRole === 'participant'
            ? "Un participant n'a pas d'organisation : le compte en sera détaché, et perdra l'accès à pro.logfit.eu."
            : 'Un organisateur doit être rattaché à une organisation ; sans organisation, le serveur refusera.'
        }
        confirmLabel="Changer le rôle"
        loading={savingRole}
        onConfirm={confirmRole}
      />
    </div>
  );
};

export default AdminUserEditor;
